import {FC, MouseEventHandler, useCallback, useEffect, useMemo, useState} from 'react';
import {hasNoExistingCategory, isPrivateTemplateVisible} from '../taskPrivacy';
import './TaskTemplates.less';
import * as DateUtil from '@/lib/dateutils';
import * as TaskActions from '../../store/actions/taskActions';
import * as TaskTemplateActions from '../../store/actions/templateTaskActions';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Button from '@/ui/Buttons/Button';
import IconButton from '@/ui/IconButton/IconButton';
import Icons from '@/ui/IconButton/Icons';
import Searchbar, {SearchOptions} from '../../Search/Searchbar';
import Task from '@/entities/task';
import {stripTemplateHtml} from '@/TaskManagement/TaskHelper';
import TaskTemplateItem from './TaskTemplateItem';
import TemplateTask from '@/entities/templateTask';
import locals from "@/lib/localStorage";
import util from '@/lib/util';
import {DialogType} from "@/store/dialogs";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Typography} from "@mui/material";
import {dialogStore} from "@/store/zstore";
import {confirmWarningOkCancel} from '@/ui/Confirm';
import {connect} from 'react-redux';
import {deleteTemplateTask} from '@/store/deps/templateTaskDeps';
import {existsFilter} from "@/lib/picos/filters";
import {faTimes} from '@fortawesome/pro-light-svg-icons';
import {getCurrentFirmId, getCurrentUserId} from "@/lib/picos/currentLogin";
import {isMobileView} from "@/lib/picos/browserFns";
import ArrayMap from "@/lib/ArrayMap";
import Category from "@/entities/category";
import {useRootDispatch} from "@/hooks/useOutlawStore";
import {uuid} from "@/@types/schema";
import type {RootState} from "@/@types/store";
import useComponentCache from "@/hooks/useComponentCache";
import {produce} from 'immer';

export type TaskTemplatesProps = {
    // Unique key name for the internal searchbar
    keyName: string,

    // Call this function when a selection is made in chooser mode
    onClick?: Function,

    // Call this function when the cancel button is click in chooser mode
    onCancel?: MouseEventHandler<HTMLButtonElement>,

    // Should we use this in chooser mode?  This will call the onClick function
    useChooser?: boolean,

    // Show a small searchbar
    showSearch?: boolean,

    // Environment variables in an object
    env?: any,

    // From redux
    categories: ArrayMap<Category>,
    tasks: ArrayMap<Task>,
    templateTasks: ArrayMap<TemplateTask>,
};

type TaskTemplateState = {
    terms: string[],
    options: SearchOptions,
    isShown: boolean,
    categoryExpansionMap: Map<string, boolean>,
    isSearchMode: boolean
}

const TaskTemplates: FC<TaskTemplatesProps> = props => {
    if (props.useChooser) {
        if (!props.onClick) throw new Error('onClick must be provided when using useChooser');
        if (!props.onCancel) throw new Error('onCancel must be provided when using useChooser');
    }

    const {cacheSave, cacheValue} = useComponentCache<TaskTemplateState>(`task-templates-${props.keyName?.toLowerCase().replaceAll(' ', '-')}`);

    const [terms, setTerms] = useState<Array<string>>(cacheValue('terms', []));
    const [options, setOptions] = useState<SearchOptions>(cacheValue('options', {}));
    const [isShown, setIsShown] = useState<boolean>(cacheValue('isShown', true));
    const [categoryExpansionMap, setCategoryExpansionMap] = useState<Map<string, boolean>>(cacheValue('categoryExpansionMap', new Map()));
    const [isSearchMode, setIsSearchMode] = useState<boolean>(cacheValue('isSearchMode', false));

    const dispatch = useRootDispatch();

    // Save state
    useEffect(() => {
        return () => cacheSave({terms, options, isShown, categoryExpansionMap, isSearchMode});
    }, [cacheSave, terms, options, isShown, categoryExpansionMap, isSearchMode]);

    // Set the map of expanded categories
    useEffect(() => {
            props.categories.forEach((category) => {
                let hasChanged = false;
                const cem = produce(categoryExpansionMap, cem => {
                    if (categoryExpansionMap.get(category.name) !== locals.getBoolean(category.name, false) ||
                        categoryExpansionMap.get('AccordionFavorites_' + category.name) !== locals.getBoolean('AccordionFavorites_' + category.name, false) ||
                        categoryExpansionMap.get('AccordionFavorites') !== locals.getBoolean('AccordionFavorites', false)
                    ) {
                        hasChanged = true;

                        cem.set(category.name, locals.getBoolean(category.name, false));
                        cem.set('AccordionFavorites_' + category.name, locals.getBoolean('AccordionFavorites_' + category.name, false));
                        cem.set('AccordionFavorites', locals.getBoolean('AccordionFavorites', false));
                    }
                });

                if (hasChanged) setCategoryExpansionMap(cem);
            });
        },
        [terms, options, isShown, categoryExpansionMap, props.categories, setCategoryExpansionMap]
    );

    // Open the task editor with the new task (global mode)
    const handleAddTask = useCallback((task: TemplateTask) => {
        if (task.isValid()) {
            dispatch(TaskTemplateActions.saveTemplateTaskQueue(task));
            dialogStore.actions.close();
        }
        else console.error(task.validationError);
    }, [dispatch]);

    const cancelDialog = useCallback(() => {
        dialogStore.actions.close();
    }, []);

    /**
     * Add a template task
     *
     * @param {Event} e
     */
    const onAdd = useCallback(() => {
        dialogStore.actions.openTypedDialog(DialogType.Task, {
            title: 'Add Task Template',
            dialogType: DialogType.Task,
            componentProps: {
                current: new TemplateTask({
                    firmId: getCurrentFirmId(),
                    taskId: util.genUUID(),
                    addedOn: util.newHRTimestamp(),
                    addedBy: getCurrentUserId(),
                    title: '',
                    urgency: 'M',
                    impact: 'M',
                    assignedTo: getCurrentUserId(),
                    status: 'C',
                    tieIns: [],
                    notification: {},
                    participants: []
                }),
                embedded: true,
                presets: {
                    categories: [],
                    tieIns: []
                },
            },
            onOk: handleAddTask,
            onCancel: cancelDialog,
        });
    }, [handleAddTask, cancelDialog]);

    const onSaveTaskTemplateFromDialog = useCallback((templateTask: TemplateTask) => {
        // Save the task template
        if (templateTask.isValid()) dispatch(TaskTemplateActions.saveTemplateTaskQueue(templateTask));
        else console.error(templateTask.validationError);

        // Hide the dialog (if shown);
        dialogStore.actions.close();
    }, [dispatch]);

    /**
     * Edit this template task.
     *
     * @param {TemplateTask} task
     */
    const onEdit = useCallback((templateTask: TemplateTask) => {
        dialogStore.actions.openTypedDialog(DialogType.TemplateTask, {
            title: 'Edit Task Template',
            dialogType: DialogType.TemplateTask,
            fitWidth: false,
            fitHeight: true,
            componentProps: {
                current: templateTask,
                onOk: onSaveTaskTemplateFromDialog,
                onCancel: cancelDialog,
                embedded: true,
                presets: {
                    categories: [],
                    tieIns: []
                }
            },
            onOk: onSaveTaskTemplateFromDialog,
            onCancel: cancelDialog,
        });

    }, [onSaveTaskTemplateFromDialog, cancelDialog]);

    /**
     * Delete the task template.
     *
     * @param {TemplateTask} task
     */
    const onDelete = useCallback((templateTask: TemplateTask) => {
        confirmWarningOkCancel('Confirm Delete', <p>Are you sure you want to delete <b>{templateTask.title}</b>?</p>,
            'OK', 'Cancel',
            () => {
                deleteTemplateTask(templateTask);
            },
            util.noop
        );
    }, []);

    const createTask = useCallback((task: Task) => {
        dispatch(TaskActions.saveTaskQueue(task));
    }, [dispatch]);

    /**
     * Create a new task from template
     *
     * @param {TemplateTask} templateTask
     */
    const onCreateTask = useCallback((templateTask: TemplateTask) => {
        const {useChooser} = props;

        const task = stripTemplateHtml(new Task({
            ...templateTask,
            firmId: getCurrentFirmId(),
            taskId: util.genUUID(),
            createdBy: getCurrentUserId(),
            createdOn: new Date(),
            addedOn: util.newHRTimestamp(),
            addedBy: getCurrentUserId(),
            dbAddedOn: null,
            status: 'C',
            dueDate: DateUtil.endOfDay(new Date(), true),
            fromTemplateId: templateTask.id,
            tieIns: [],
            notification: {},
            participants: [],
        }));

        if (useChooser) props.onClick(task);
        else {
            dialogStore.actions.openTypedDialog(DialogType.Task, {
                title: 'Create Task From Template',
                dialogType: DialogType.Task,
                componentProps: {
                    current: task,
                    embedded: true,
                    noCloseWarning: true,
                    presets: {
                        categories: [],
                        tieIns: []
                    }
                },
                onOk: createTask,
                onCancel: cancelDialog,
            });
        }
    }, [props, createTask, cancelDialog]);

    const onAccordionChange = (name: string, isExpanded: boolean) => {
        locals.setBoolean(name, isExpanded);
    }

    /**
     * Hide the component.
     */
    const onHide = useCallback(() => {
        setIsShown(false);
    }, [setIsShown]);

    /**
     * Show the component.
     */
    const onShow = useCallback(() => {
        setIsShown(true);
    }, [setIsShown]);

    const onCategoryToggle = useCallback((category: string) => {
        const m = new Map<string, boolean>(categoryExpansionMap);
        m.set(category, !categoryExpansionMap.get(category));
        setCategoryExpansionMap(m);
    }, [categoryExpansionMap, setCategoryExpansionMap]);

    /**
     * Filter out non-global templates that were not created by us.
     *
     * @param {TemplateTask} task
     */
    const filterPrivate = useCallback((templateTask: TemplateTask) => {
        return isPrivateTemplateVisible(templateTask, getCurrentUserId());
    }, []);

    const onSearch = useCallback((terms: string[], options: SearchOptions) => {
        if (!terms || terms.length === 0) setIsSearchMode(false);
        else setIsSearchMode(true);

        setTerms(terms);
        setOptions(options);
    }, [setIsSearchMode, setOptions, setTerms]);

    const renderTemplateTask = useCallback((templateTask: TemplateTask) => {
        return <TaskTemplateItem key={templateTask.id}
                                 task={templateTask}
                                 useChooser={props.useChooser}
                                 onCreateTask={onCreateTask}
                                 onEdit={onEdit}
                                 onDelete={onDelete}
        />;
    }, [props.useChooser, onCreateTask, onEdit, onDelete]);

    const {useChooser} = props;
    const hasFavorites = props.templateTasks?.find(tt => tt.favoritedBy.includes(getCurrentUserId())) ?? false;

    // Get a list of template tasks
    const templates: TemplateTask[] = useMemo(() => (props.templateTasks ?? [] as TemplateTask[])
            .filter(util.existsAndNotDeletedFilter)
            .filter(filterPrivate)
            .filter(tt => tt.categories.every(cid => !!props.categories.get(cid)))
            .filter(tt => isSearchMode ? tt.search(terms, options) : true)
            .filter((tt: TemplateTask) => {
                if (props.env?.topLevel) {
                    let area = '';
                    switch (props.env?.topLevel) {
                        case 'matter':
                            area = 'Client';
                            break;
                        case 'lead':
                            area = 'Sales';
                            break;
                        case 'campaign':
                            area = 'Marketing';
                            break;
                        default:
                            area = '';
                    }

                    if (area) {
                        return tt.categories.map((cid: uuid) => props.categories.find(c => c.id === cid))
                            .some((cat) => cat.relatedAreas.includes(area));
                    }
                    else return true;
                }
                else return true;
            })
            .sort((a, b) => a.title.localeCompare(b.title)),
        [filterPrivate, isSearchMode, options, props.categories, props.env?.topLevel, props.templateTasks, terms]);

    // Create tasks by category. Do not memoize, as we add to it later.
    const categories = templates.map(tt => tt.categories)
        .flat()
        .filter(util.distinctFilter);

    const tasksByCategory = categories
        .filter(c => !!c)
        .map(cid => {
            return {
                category: props.categories.get(cid)?.name || 'Other',
                tasks: templates.filter(tt => (tt.categories ?? []).includes(cid)),
                favorites: templates.filter(tt => tt.favoritedBy.includes(getCurrentUserId()) && (tt.categories ?? []).includes(cid))
            };
        });

    const noCategoryTasks = templates.filter(tt => hasNoExistingCategory(tt, props.categories));

    if (noCategoryTasks.length > 0) tasksByCategory.push({
        category: '(None)',
        tasks: noCategoryTasks,
        favorites: []
    });

    const toggle = useMemo(() => useChooser
                                 ? null
                                 : isShown
                                   ? <IconButton type={Icons.DOUBLEUP} label='Task Templates' labelBold={true}
                                                 onClick={onHide}/>
                                   : <IconButton type={Icons.DOUBLEDOWN} label='Task Templates' labelBold={true}
                                                 onClick={onShow}/>,
        [isShown, onHide, onShow, useChooser]);

    const chooser = useChooser ? 'chooser' : '';

    return <div className={`task-templates ${chooser}`}>

        {/* Show Chooser*/}
        {useChooser && <div className='task-templates__cancel'>
			<Button label={<FontAwesomeIcon icon={faTimes}/>}
			        severity='danger'
			        type='circle'
			        size='medium'
			        onClick={props.onCancel}
			        title='Cancel'
			/>
		</div>
        }

        {/* Show Chooser Bar */}
        <div className={`task-templates__title ${chooser}`}>
            <div>{useChooser
                  ? isMobileView()
                    ? 'Template'
                    : 'Choose Template'
                  : toggle
            }</div>

            {isShown && useChooser &&
				<Searchbar onSearch={onSearch} keyName={props.keyName}/>
            }
            {isShown && !useChooser &&
				<IconButton type={Icons.ADD}
				            label='New'
				            onClick={onAdd}
				/>}
        </div>

        {/* Show SearchBar */}
        {isShown && props.showSearch &&
			<div className="task-templates__search-container">
				<Searchbar onSearch={onSearch} showOptions={false} keyName={props.keyName}/>
			</div>
        }

        {/* Show Favorites */}
        {hasFavorites && isShown &&
			<Accordion id='AccordionFavorites'
			           sx={{backgroundColor: 'var(--sl-color-amber-50)', mb: 1}}
			           expanded={isSearchMode ? true : locals.getBoolean('AccordionFavorites', true)}
			           onChange={(_, isExpanded) => {
                           onAccordionChange('AccordionFavorites', isExpanded);
                           onCategoryToggle('AccordionFavorites')
                       }}
			>

				<AccordionSummary expandIcon={<ArrowDropDownIcon/>}>
					<IconButton type={Icons.STAR_FILLED} color='white'/>
					<Typography style={{fontWeight: 'bold', marginLeft: '.5em'}}>Favorites</Typography>
				</AccordionSummary>

				<AccordionDetails id='task-templates-favorites'>
                    {tasksByCategory.filter(existsFilter)
                        .filter(tbc => tbc.favorites.length > 0)
                        .map(tbc => (
                            <Accordion id={`AccordionFavorites_${tbc.category}`}
                                       key={`AccordionFavorites_${tbc.category}`}
                                       sx={{paddingTop: '3px'}}
                                       expanded={isSearchMode ? true : locals.getBoolean(`AccordionFavorites_${tbc.category}`, true)}
                                       onChange={(_, isExpanded) => onAccordionChange(`AccordionFavorites_${tbc.category}`, isExpanded)}
                            >
                                <AccordionSummary expandIcon={<ArrowDropDownIcon/>}
                                                  aria-controls='favorite-categories-content'
                                                  onClick={() => onCategoryToggle('AccordionFavorites_' + tbc.category)}
                                >
                                    <Typography style={{fontWeight: 600}}>{tbc.category}</Typography>
                                </AccordionSummary>

                                <AccordionDetails>
                                    {tbc.favorites.map(tt => <TaskTemplateItem key={tt.id}
                                                                               task={tt}
                                                                               useChooser={props.useChooser}
                                                                               onCreateTask={onCreateTask}
                                                                               onEdit={onEdit}
                                                                               onDelete={onDelete}
                                    />)}
                                </AccordionDetails>

                            </Accordion>
                        ))}
				</AccordionDetails>

			</Accordion>
        }

        {/* Show Task Templates by Category */}
        {isShown &&
            tasksByCategory.map(tbc => (
                <Accordion id={`AccordionMain_${tbc.category || 'Other'}`}
                           key={`AccordionMain_${tbc.category || 'Other'}`}
                           expanded={isSearchMode ? true : locals.getBoolean(`AccordionMain_${tbc.category || 'Other'}`, true)}
                           onChange={(_, isExpanded) => onAccordionChange(`AccordionMain_${tbc.category}`, isExpanded)}
                >
                    <AccordionSummary expandIcon={<ArrowDropDownIcon/>}
                                      aria-controls='categories-content'
                                      onClick={() => onCategoryToggle(tbc.category || 'Other')}
                    >
                        <Typography style={{fontWeight: 'bold'}}>{tbc.category || 'Other'}</Typography>
                    </AccordionSummary>
                    <AccordionDetails key={tbc.category || 'Other'}>
                        {tbc.tasks.map(tt => renderTemplateTask(tt))}
                    </AccordionDetails>
                </Accordion>
            ))
        }
    </div>
}


const mapStateToProps = (state: RootState) => {
    return {
        categories: state.categories,
        tasks: state.tasks,
        templateTasks: state.templateTasks
    }
};

export default connect(mapStateToProps)(TaskTemplates);
